import { html, nothing } from 'lit';
import { ago } from '../../shared/ui/format.js';
import { issueProjectToken, revokeProjectToken } from '../api/developer.js';
import { AccountView } from './account-view.js';

/**
 * The account's project tokens: what CI authenticates with.
 *
 * A table and one action. A token is minted here and shown once - it is stored as a hash, so there is
 * no reading it again - and revoked here, which leaves the row stamped rather than gone.
 */
export class UserAccountTokens extends AccountView
{
   static section = 'tokens';

   static properties = { issuing: { state: true } };

   constructor()
   {
      super();
      this.issuing = false;
   }

   get pageActions()
   {
      return [{
         id: 'token',
         label: 'Issue a token',
         icon: 'fa-solid fa-plus',
         variant: 'primary',
         disabled: this.busy
      }];
   }

   onPageAction(id)
   {
      if (id === 'token')
      {
         this.issuing = true;
      }
   }

   issue(event)
   {
      event.preventDefault();

      const form = new FormData(event.target);
      const name = String(form.get('name') ?? '').trim();
      const remoteKey = String(form.get('remoteKey') ?? '').trim();

      if (!name)
      {
         return;
      }

      this.issuing = false;

      void this.act(
         () => issueProjectToken(this.account, { name, remoteKey: remoteKey || null }),
         issued => ({
            notice: { variant: 'success', message: `Issued ${issued.name}.` },
            secret: {
               label: `Project token ${issued.name}`,
               note: 'Shown once. It is stored as a hash, so there is no way to read it again - put it in '
                  + 'your CI\'s secret store now, or issue another.',
               value: issued.token
            }
         }));
   }

   get body()
   {
      const rows = this.team.tokens ?? [];

      return html`
         ${this.issuing ? this.issueForm : nothing}
         <lia-card>
            <lia-data-table
               listing-id="account-tokens"
               .perPage=${10}
               no-column-manager
               ?loading=${this.busy}
               .columns=${[
                  { key: 'name', label: 'Name', sortable: true, searchable: true },
                  { key: 'prefix', label: 'Token', render: row => html`<code>${row.prefix}…</code>` },
                  { key: 'remoteKey', label: 'Limited to', render: row => row.remoteKey || 'Whole team' },
                  { key: 'createdAt', label: 'Issued', sortable: true, render: row => ago(row.createdAt) },
                  { key: 'lastUsedAt', label: 'Last activity', sortable: true, render: row => (row.lastUsedAt ? ago(row.lastUsedAt) : 'None yet') },
                  {
                     key: 'revokedAt',
                     label: 'State',
                     sortable: true,
                     render: row => (row.revokedAt
                        ? html`<lia-badge variant="secondary">Revoked</lia-badge>`
                        : html`<lia-badge variant="success">Active</lia-badge>`)
                  }
               ]}
               .rows=${rows}
               .rowKey=${row => row.id}
               empty-title="No project tokens"
               empty-message="CI presents one to say which team its runs belong to."
               .emptyActions=${[{ id: 'token', label: 'Issue a token', icon: 'fa-solid fa-plus', variant: 'primary' }]}
               .rowActions=${row => (row.revokedAt
                  ? []
                  : [{
                     id: 'revoke',
                     icon: 'fa-solid fa-ban',
                     title: 'Revoke this token',
                     variant: 'outline-danger',
                     size: 'sm',
                     disabled: this.busy,
                     confirm: {
                        title: `Revoke ${row.name}?`,
                        message: `Any CI presenting ${row.prefix}… stops resolving to this team on its next run.`,
                        confirmLabel: 'Revoke',
                        variant: 'danger'
                     }
                  }])}
               @lia-action=${event =>
               {
                  const action = event.detail?.action;
                  const row = event.detail?.row;

                  if (action?.id === 'token')
                  {
                     this.issuing = true;
                  }
                  else if (row)
                  {
                     void this.act(() => revokeProjectToken(this.account, row.id), `Revoked ${row.name}.`);
                  }
               }}
            ></lia-data-table>
         </lia-card>`;
   }

   get issueForm()
   {
      return html`
         <lia-card title="Issue a token" icon="fa-solid fa-key">
            <form class="row g-2 align-items-end" @submit=${this.issue}>
               <div class="col-sm-4">
                  <label class="form-label small mb-1" for="token-name">Name</label>
                  <input
                     id="token-name"
                     class="form-control form-control-sm"
                     name="name"
                     placeholder="jenkins-main"
                     required
                     maxlength="120"
                     autofocus
                     ?disabled=${this.busy}
                  >
               </div>
               <div class="col-sm-5">
                  <label class="form-label small mb-1" for="token-remote">Limit to one repository (optional)</label>
                  <input
                     id="token-remote"
                     class="form-control form-control-sm"
                     name="remoteKey"
                     placeholder="github.com/liasoft/covdbg-quick-start"
                     maxlength="500"
                     ?disabled=${this.busy}
                  >
               </div>
               <div class="col-sm-3 d-flex gap-2">
                  <button class="btn btn-sm btn-primary" type="submit" ?disabled=${this.busy}>Issue the token</button>
                  <button class="btn btn-sm btn-link" type="button" @click=${() => { this.issuing = false; }}>
                     Cancel
                  </button>
               </div>
            </form>
         </lia-card>`;
   }
}

customElements.define('user-account-tokens', UserAccountTokens);
